import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Close, LocalHospital, Add, ExpandMore } from '@mui/icons-material';
import { type DiagnosisResult } from '../../contexts/api-contexts/CasesContext';
import {
  useDiseases,
  type Disease,
} from '../../contexts/api-contexts/DiseasesContext';

interface CaseDiagnosisInfoDialogProps {
  open: boolean;
  onClose: () => void;
  diagnosis: DiagnosisResult | null;
  onAddToSelected: (diagnosis: DiagnosisResult) => void;
  isSelected: boolean;
}

const CaseDiagnosisInfoDialog: React.FC<CaseDiagnosisInfoDialogProps> = ({
  open,
  onClose,
  diagnosis,
  onAddToSelected,
  isSelected,
}) => {
  const { fetchDisease, getDisease, isLoading, error, clearError } =
    useDiseases();
  const [diseaseInfo, setDiseaseInfo] = useState<Disease | null>(null);

  // Fetch disease information when dialog opens
  useEffect(() => {
    if (open && diagnosis) {
      const existingDisease = getDisease(diagnosis.id);
      if (existingDisease) {
        setDiseaseInfo(existingDisease);
      } else {
        // Fetch disease information
        fetchDisease(diagnosis.id).then((disease) => {
          if (disease) {
            setDiseaseInfo(disease);
          }
        });
      }
    }
  }, [open, diagnosis, fetchDisease, getDisease]);

  // Clear disease info when dialog closes
  useEffect(() => {
    if (!open) {
      setDiseaseInfo(null);
      clearError();
    }
  }, [open, clearError]);

  if (!diagnosis) {
    return null;
  }

  // Helper function to get probability color
  const getProbabilityColor = (probability: number) => {
    if (probability >= 0.8) return 'success';
    if (probability >= 0.5) return 'warning';
    return 'default';
  };

  const handleAddToSelected = () => {
    onAddToSelected(diagnosis);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospital sx={{ color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Diagnosis Information
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Diagnosis Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {diagnosis.name}
          </Typography>

          {/* Symptom match score Chip */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={`Symptom match score: ${Math.round(
                diagnosis.probability
              )}`}
              color={getProbabilityColor(diagnosis.probability)}
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Disease Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Disease Information
          </Typography>

          {/* Loading State */}
          {isLoading(diagnosis.id) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading disease information...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && !isLoading(diagnosis.id) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load disease information: {error}
            </Alert>
          )}

          {/* Disease Information Content */}
          {diseaseInfo && !isLoading(diagnosis.id) && (
            <Box>
              {/* Basic Information */}
              {diseaseInfo.info && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Description
                  </Typography>
                  <Typography variant="body1">{diseaseInfo.info}</Typography>
                </Box>
              )}

              {/* Animal Restrictions */}
              {(diseaseInfo.only_female ||
                diseaseInfo.only_male ||
                diseaseInfo.only_neutered ||
                diseaseInfo.only_non_neutered) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Animal Restrictions
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {diseaseInfo.only_female && (
                      <Chip
                        label="Female only"
                        size="small"
                        color="secondary"
                      />
                    )}
                    {diseaseInfo.only_male && (
                      <Chip label="Male only" size="small" color="secondary" />
                    )}
                    {diseaseInfo.only_neutered && (
                      <Chip
                        label="Neutered only"
                        size="small"
                        color="secondary"
                      />
                    )}
                    {diseaseInfo.only_non_neutered && (
                      <Chip
                        label="Non-neutered only"
                        size="small"
                        color="secondary"
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Additional Information */}
              {diseaseInfo.area_specified && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label="Geographic area specific"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Detailed Information Accordions */}
              <Box sx={{ mt: 2 }}>
                {diseaseInfo.how_to_confirm && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        How to Confirm
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        {diseaseInfo.how_to_confirm}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {diseaseInfo.how_to_exclude && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        How to Exclude
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        {diseaseInfo.how_to_exclude}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {diseaseInfo.treatment_info && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        Treatment Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        {diseaseInfo.treatment_info}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {diseaseInfo.follow_up && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">Follow Up</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2">
                        {diseaseInfo.follow_up}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Disease Causes */}
                {diseaseInfo.disease_causes &&
                  diseaseInfo.disease_causes.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          Disease Causes ({diseaseInfo.disease_causes.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {diseaseInfo.disease_causes.map((cause) => (
                            <ListItem key={cause.id}>
                              <ListItemText primary={cause.name} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                {/* Sources */}
                {diseaseInfo.sources && diseaseInfo.sources.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        Sources ({diseaseInfo.sources.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {diseaseInfo.sources.map((source) => (
                          <ListItem key={source.id}>
                            <ListItemText primary={source.name} />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            </Box>
          )}

          {/* No disease information available */}
          {!diseaseInfo && !isLoading(diagnosis.id) && !error && (
            <Typography variant="body2" color="text.secondary">
              No detailed disease information available.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {!isSelected && (
          <Button
            onClick={handleAddToSelected}
            variant="contained"
            startIcon={<Add />}
            sx={{ ml: 1 }}
          >
            Add to Selected Diagnoses
          </Button>
        )}
        {isSelected && (
          <Button disabled variant="contained" sx={{ ml: 1 }}>
            Already Selected
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CaseDiagnosisInfoDialog;
